package fr.frux.core.network.auth

import io.ktor.client.HttpClient
import io.ktor.client.call.HttpClientCall
import io.ktor.client.plugins.HttpClientPlugin
import io.ktor.client.plugins.HttpSend
import io.ktor.client.plugins.plugin
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpStatusCode
import io.ktor.util.AttributeKey

/** Empêche un deuxième rejeu sur la même requête : un seul rejeu, jamais de boucle (#26). */
private val RetriedAfterRefreshAttribute = AttributeKey<Unit>("FruxRetriedAfterRefresh")

/**
 * Rejeu réactif après un 401 sur une requête authentifiée (#26, ADR 0019).
 *
 * Lit `call.response.status` directement, jamais un `catch` d'exception (prouvé par test, pas par
 * lecture du code Ktor) : `HttpCallValidator` (qui convertit un 401 en [UnauthorizedException] via
 * `HttpResponseValidator` dans `installFruxDefaults`) s'installe *avant* ce plugin dans
 * `HttpClient.init` (Ktor installe ses plugins internes puis le `HttpSend` de l'utilisateur), donc
 * son hook `HttpSend` est plus **externe** que celui-ci. Un `catch` ici ne verrait jamais rien :
 * l'exception n'est levée qu'une fois que ce hook a déjà rendu la main avec l'`HttpClientCall` brut,
 * pas avant. La lecture directe du statut, elle, fonctionne dans les deux sens : on peut décider de
 * rejouer *avant* que le validateur externe n'ait even la chance de convertir le 401 en exception.
 *
 * Le header `Authorization` est reposé explicitement après le refresh : Ktor ne relit pas la
 * configuration de la requête entre deux appels de `execute()` sur le même [HttpRequestBuilder],
 * il rejoue tel quel ce qui a été construit par [AuthInterceptor] au premier passage.
 */
class FruxTokenRefresh private constructor(
    private val tokenRefresher: TokenRefresher,
) {
    class Config {
        var tokenRefresher: TokenRefresher? = null
    }

    companion object Plugin : HttpClientPlugin<Config, FruxTokenRefresh> {
        override val key: AttributeKey<FruxTokenRefresh> = AttributeKey("FruxTokenRefresh")

        override fun prepare(block: Config.() -> Unit): FruxTokenRefresh {
            val config = Config().apply(block)
            return FruxTokenRefresh(
                tokenRefresher =
                    config.tokenRefresher
                        ?: error("TokenRefresher must be provided to FruxTokenRefresh"),
            )
        }

        override fun install(
            plugin: FruxTokenRefresh,
            scope: HttpClient,
        ) {
            val send = scope.plugin(HttpSend)
            send.intercept { request ->
                var call = execute(request)

                if (plugin.shouldRetry(request, call)) {
                    request.attributes.put(RetriedAfterRefreshAttribute, Unit)
                    val staleAccessToken =
                        request.headers[HttpHeaders.Authorization]?.removePrefix("Bearer ")
                    when (val outcome = plugin.tokenRefresher.refresh(staleAccessToken)) {
                        is RefreshOutcome.Refreshed -> {
                            request.headers.remove(HttpHeaders.Authorization)
                            request.headers.append(
                                HttpHeaders.Authorization,
                                "Bearer ${outcome.tokens.accessToken}",
                            )
                            call = execute(request)
                        }

                        // Session morte (401/400 côté /refresh) : le 401 d'origine remonte tel
                        // quel, c'est ce que le serveur a effectivement répondu.
                        RefreshOutcome.SessionDead -> {
                            Unit
                        }

                        // Panne transitoire (#26, C2) : laisser remonter le 401 d'origine le
                        // traduirait en `UnauthorizedException`, donc en session morte — contraire
                        // à l'ADR (« réseau/5xx/429 propagé tel quel, jamais une session morte »).
                        // La cause d'origine (exception réseau ou `ResponseException` du refresh)
                        // porte l'information réelle et se traduit par `AppError.Network`/`Server`.
                        is RefreshOutcome.TransientFailure -> {
                            throw outcome.cause
                        }
                    }
                }

                call
            }
        }
    }

    private fun shouldRetry(
        request: HttpRequestBuilder,
        call: HttpClientCall,
    ): Boolean =
        call.response.status == HttpStatusCode.Unauthorized &&
            request.attributes.contains(AuthenticatedRequestAttribute) &&
            !request.attributes.contains(RetriedAfterRefreshAttribute)
}
